import { useEffect, useRef, useState } from "react";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

type FilePreviewProps = {
  src?: string;
  title?: string;
  fileName?: string;
};

const getUrlPath = (value: string) => {
  try {
    return new URL(value).pathname.toLowerCase();
  } catch {
    return value.split("?")[0].split("#")[0].toLowerCase();
  }
};

const isImageFile = (value: string) => {
  const path = getUrlPath(value);
  return /\.(png|jpg|jpeg|webp|gif)$/i.test(path) || value.startsWith("data:image/");
};

const isPdfFile = (value: string) => {
  const path = getUrlPath(value);
  return path.endsWith(".pdf") || value.startsWith("data:application/pdf");
};

export function FilePreview({ src, title = "Document Preview", fileName }: FilePreviewProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const isPdf = Boolean(src && (isPdfFile(src) || (fileName && isPdfFile(fileName))));

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [src]);

  if (!src) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-slate-700">
        No file available
      </div>
    );
  }

  if (isImageFile(src)) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-linear-to-b from-slate-100 to-white">
        {isLoading ? (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/80 backdrop-blur-sm">
            <span className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-emerald-600" />
            <p className="text-sm font-medium text-slate-700">Loading preview...</p>
          </div>
        ) : null}

        {hasError ? (
          <div className="flex h-[50vh] items-center justify-center p-6 text-center text-sm text-red-600">
            Unable to load image preview.
          </div>
        ) : (
          <img
            src={src}
            alt={title}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setHasError(true);
            }}
            className="h-[50vh] w-full object-contain"
          />
        )}
      </div>
    );
  }

  if (isPdf) {
    return <PdfPages key={src} src={src} title={title} />;
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
      >
        Open File in New Tab
      </a>
    </div>
  );
}

function PdfPages({ src, title }: { src: string; title: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(75);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const controller = new AbortController();
    let task: import("pdfjs-dist").PDFDocumentLoadingTask | undefined;
    let rendering: import("pdfjs-dist").RenderTask | undefined;

    const renderPages = async () => {
      try {
        const pdfjs = await import("pdfjs-dist");
        if (controller.signal.aborted) return;
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
        // Fetch the signed URL without application authorization headers.
        const response = await fetch(src, { signal: controller.signal });
        if (!response.ok) throw new Error("PDF request failed");
        const data = await response.arrayBuffer();
        if (controller.signal.aborted) return;
        task = pdfjs.getDocument({ data });
        const pdf = await task.promise;
        for (let number = 1; number <= pdf.numPages; number++) {
          if (controller.signal.aborted) return;
          const page = await pdf.getPage(number);
          if (controller.signal.aborted) return;
          const canvas = document.createElement("canvas");
          const original = page.getViewport({ scale: 1 });
          const width = Math.min(container.clientWidth || 850, 1200);
          const scale = (width / original.width) * Math.min(window.devicePixelRatio || 1, 2);
          const viewport = page.getViewport({ scale });
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          canvas.className = "block h-auto w-full bg-white shadow-sm";
          canvas.setAttribute("role", "img");
          canvas.setAttribute("aria-label", title + " — page " + number + " of " + pdf.numPages);
          rendering = page.render({ canvas, viewport });
          await rendering.promise;
          if (controller.signal.aborted) return;
          container.appendChild(canvas);
          page.cleanup();
          setLoading(false);
        }
      } catch {
        if (!controller.signal.aborted) {
          setFailed(true);
          setLoading(false);
        }
      }
    };
    void renderPages();
    return () => {
      controller.abort();
      rendering?.cancel();
      void task?.destroy();
      container.replaceChildren();
    };
  }, [src, title]);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
      {loading && (
        <div role="status" className="flex h-48 items-center justify-center text-sm text-slate-700">
          Loading PDF...
        </div>
      )}
      <div
        className="sticky top-0 z-10 flex items-center justify-end gap-2 border-b border-slate-200 bg-white px-3 py-2"
        role="group"
        aria-label="PDF zoom controls"
      >
        <button
          type="button"
          aria-label="Zoom out"
          disabled={loading || failed || zoom <= 50}
          onClick={() => setZoom((value) => Math.max(50, value - 25))}
          className="rounded px-3 py-1 text-sm hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-blue-600 disabled:opacity-40"
        >
          −
        </button>
        <span className="min-w-12 text-center text-xs text-slate-600" aria-live="polite">
          {zoom}%
        </span>
        <button
          type="button"
          aria-label="Zoom in"
          disabled={loading || failed || zoom >= 200}
          onClick={() => setZoom((value) => Math.min(200, value + 25))}
          className="rounded px-3 py-1 text-sm hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-blue-600 disabled:opacity-40"
        >
          +
        </button>
        <button
          type="button"
          aria-label="Reset zoom to fit width"
          disabled={loading || failed || zoom === 100}
          onClick={() => setZoom(100)}
          className="rounded px-3 py-1 text-xs hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-blue-600 disabled:opacity-40"
        >
          Reset
        </button>
      </div>
      <div className="max-h-[65vh] overflow-auto">
        <div ref={containerRef} className="mx-auto space-y-3" style={{ width: zoom + "%" }} />
      </div>
      {failed && (
        <p role="alert" className="p-6 text-center text-sm text-red-600">
          Unable to load PDF preview. Please try again or use the download button.
        </p>
      )}
    </div>
  );
}
